'use client';

import { useCallback, useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from '@/components/ui/carousel';
import { SafeRemoteImage } from '@/components/safe-remote-image';
import {
  catalogHeaderImagesToCarouselSlides,
  CATALOG_HEADER_WEB_ASPECT,
  CATALOG_HEADER_MOBILE_ASPECT,
} from '@/lib/catalogHeaderImages';
import type { StoreHeaderCarouselSlide } from '@/components/store-header-carousel';
import { cn } from '@/lib/utils';

const AUTO_PLAY_MS = 5000;

type CatalogHeaderImagesCarouselProps = {
  images: unknown;
  className?: string;
  onBannerClick?: () => void;
};

function BannerSlide({
  slide,
  index,
  onClick,
}: {
  slide: StoreHeaderCarouselSlide;
  index: number;
  onClick?: () => void;
}) {
  return (
    <Card className="overflow-hidden py-0">
      <CardContent className="p-0">
        <button
          type="button"
          onClick={onClick}
          className="block w-full cursor-pointer border-0 bg-transparent p-0 text-left"
          aria-label={`Banner ${index + 1}`}
        >
          <div
            className="relative w-full overflow-hidden rounded-4xl md:hidden"
            style={{ aspectRatio: String(CATALOG_HEADER_MOBILE_ASPECT) }}
          >
            <SafeRemoteImage
              src={slide.mobileUrl}
              alt=""
              fill
              sizes="100vw"
              className="object-cover"
            />
          </div>
          <div
            className="relative hidden w-full overflow-hidden rounded-4xl md:block"
            style={{ aspectRatio: String(CATALOG_HEADER_WEB_ASPECT) }}
          >
            <SafeRemoteImage
              src={slide.webUrl}
              alt=""
              fill
              sizes="100vw"
              className="object-cover"
            />
          </div>
        </button>
      </CardContent>
    </Card>
  );
}

function CarouselProgress({
  count,
  activeIndex,
  progress,
}: {
  count: number;
  activeIndex: number;
  progress: number;
}) {
  return (
    <div
      className="pointer-events-none absolute inset-x-5 bottom-2 z-10 flex gap-1"
      aria-hidden
    >
      {Array.from({ length: count }).map((_, index) => {
        const fill =
          index < activeIndex ? 1 : index === activeIndex ? progress : 0;

        return (
          <div
            key={index}
            className="h-0.5 flex-1 overflow-hidden rounded-full bg-white/20"
          >
            <div
              className="h-full rounded-full bg-white/55"
              style={{ width: `${fill * 100}%` }}
            />
          </div>
        );
      })}
    </div>
  );
}

export function CatalogHeaderImagesCarousel({
  images,
  className,
  onBannerClick,
}: CatalogHeaderImagesCarouselProps) {
  const slides = catalogHeaderImagesToCarouselSlides(images);
  const [api, setApi] = useState<CarouselApi>();
  const [activeIndex, setActiveIndex] = useState(0);
  const [progress, setProgress] = useState(0);

  const advanceSlide = useCallback(() => {
    if (!api) return;
    if (api.canScrollNext()) {
      api.scrollNext();
    } else {
      api.scrollTo(0);
    }
  }, [api]);

  useEffect(() => {
    if (!api || slides.length <= 1) return;

    let rafId = 0;
    let slideStart = performance.now();

    const tick = (now: number) => {
      const elapsed = now - slideStart;
      const nextProgress = Math.min(elapsed / AUTO_PLAY_MS, 1);
      setProgress(nextProgress);

      if (nextProgress >= 1) {
        advanceSlide();
        return;
      }

      rafId = requestAnimationFrame(tick);
    };

    const startTick = () => {
      cancelAnimationFrame(rafId);
      slideStart = performance.now();
      setProgress(0);
      rafId = requestAnimationFrame(tick);
    };

    const onSelect = () => {
      setActiveIndex(api.selectedScrollSnap());
      startTick();
    };

    api.on('select', onSelect);
    onSelect();

    return () => {
      cancelAnimationFrame(rafId);
      api.off('select', onSelect);
    };
  }, [api, slides.length, advanceSlide]);

  if (slides.length === 0) {
    return null;
  }

  return (
    <div className={cn('w-full p-1', className)}>
      <div className="relative overflow-hidden rounded-4xl">
        <Carousel
          className="w-full"
          setApi={setApi}
          opts={{ loop: slides.length > 1 }}
        >
          <CarouselContent className="-ml-3">
            {slides.map((slide, index) => (
              <CarouselItem
                key={`${slide.webUrl}-${slide.mobileUrl}-${index}`}
                className="basis-full pl-3"
              >
                <BannerSlide slide={slide} index={index} onClick={onBannerClick} />
              </CarouselItem>
            ))}
          </CarouselContent>
        </Carousel>
        {slides.length > 1 && (
          <CarouselProgress
            count={slides.length}
            activeIndex={activeIndex}
            progress={progress}
          />
        )}
      </div>
    </div>
  );
}
